import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

// Copied from JournalPage.tsx CustomFilters implementation to ensure the
// Calendar/Trades "Advanced" button is an exact duplicate of the Journal "Filters" button.

const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export type JournalFilters = {
  // Days/Time
  daysOfWeek: number[]; // 0-6 (Sun-Sat)
  months: number[]; // 0-11
  timeMin?: string; // 'HH:MM'
  timeMax?: string; // 'HH:MM'
  // Price/Volume
  volumeMin?: number;
  volumeMax?: number;
  // Win/Loss Trades
  pnlMin?: number;
  pnlMax?: number;
  // Placeholders for future filters (activated UI, not yet applied)
  durationMin?: string;
  durationMax?: string;
  priceMin?: number;
  priceMax?: number;
  inTradePriceMin?: number;
  inTradePriceMax?: number;
  execCountMin?: number;
  execCountMax?: number;
  gapMinPct?: number;
  gapMaxPct?: number;
  rvolMin?: number;
  rvolMax?: number;
  atrMin?: number;
  atrMax?: number;
  dayType?: string;
  entryPctAtrMin?: number;
  entryPctAtrMax?: number;
  moveIntraPctMin?: number;
  moveIntraPctMax?: number;
  priorDayRvolMin?: number;
  priorDayRvolMax?: number;
  dayResult?: string; // All | Winning | Losing
  pnlPctMin?: number;
  pnlPctMax?: number;
  pnlRMin?: number;
  pnlRMax?: number;
  tagWinPctMin?: number;
  excludedTags?: string;
  tagProfitFactorMin?: number;
  tagPnlMin?: number;
  tagPnlMax?: number;
  tagMfePosMin?: number;
  tagMfePosMax?: number;
  tagCountMin?: number;
  tagCountMax?: number;
  tagMaePosMin?: number;
  tagMaePosMax?: number;
  tagVolumeMin?: number;
  tagVolumeMax?: number;
  sma5MinPct?: number; sma5MaxPct?: number;
  sma10MinPct?: number; sma10MaxPct?: number;
  sma20MinPct?: number; sma20MaxPct?: number;
  sma50MinPct?: number; sma50MaxPct?: number;
  sma100MinPct?: number; sma100MaxPct?: number;
  sma200MinPct?: number; sma200MaxPct?: number;
  bestExitPnlMin?: number; bestExitPnlMax?: number;
  posMaeMin?: number; posMaeMax?: number;
  priceMaeMin?: number; priceMaeMax?: number;
  timeToPosMaeMin?: number; timeToPosMaeMax?: number;
  timeToPriceMaeMin?: number; timeToPriceMaeMax?: number;
  bestExitRMin?: number; bestExitRMax?: number;
  posMfeMin?: number; posMfeMax?: number;
  priceMfeMin?: number; priceMfeMax?: number;
  timeToPosMfeMin?: number; timeToPosMfeMax?: number;
  timeToPriceMfeMin?: number; timeToPriceMfeMax?: number;
  initialRiskMin?: number; initialRiskMax?: number;
  exitEfficiencyMinPct?: number; exitEfficiencyMaxPct?: number;
  commissionsMin?: number; commissionsMax?: number;
  pnlPctOfMaeMin?: number; pnlPctOfMaeMax?: number;
  pnlPctOfMfeMin?: number; pnlPctOfMfeMax?: number;
};

export const emptyJournalFilters = (): JournalFilters => ({
  daysOfWeek: [],
  months: [],
  timeMin: undefined,
  timeMax: undefined,
  volumeMin: undefined,
  volumeMax: undefined,
  pnlMin: undefined,
  pnlMax: undefined,
} as JournalFilters);

export function JournalFiltersDropdown({
  label = 'Filters',
  isDark,
  value,
  onApply,
  triggerClassName,
  activeTriggerClassName,
  badgeClassName,
  placement = 'bottom',
}: {
  label?: string;
  isDark?: boolean;
  value: JournalFilters;
  onApply: (next: JournalFilters) => void;
  triggerClassName?: string;
  activeTriggerClassName?: string;
  badgeClassName?: string;
  placement?: 'top' | 'bottom';
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<JournalFilters>(value);
  const ref = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const suppressToggleRef = useRef(false);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);
  const dark = typeof isDark === 'boolean' ? isDark : (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));

  useEffect(() => {
    const onDocDown = (e: MouseEvent) => {
      // If the click is inside the TimeField portal popover, do NOT close the Filters menu.
      // The time picker popover is rendered via portal with the class 'filters-time-pop'.
      const targetEl = e.target as Element | null;
      if (targetEl && typeof (targetEl as any).closest === 'function') {
        const inTimePopover = (targetEl as Element).closest('.filters-time-pop');
        if (inTimePopover) return;
      }
      const targetNode = e.target as Node;
      if (ref.current?.contains(targetNode) || panelRef.current?.contains(targetNode)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    days: true,
    price: true,
    instrument: true,
    winloss: true,
    tags: true,
    sma: true,
    stats: true,
  });
  const toggle = (k: keyof typeof collapsed) => setCollapsed((p) => ({ ...p, [k]: !p[k] }));

  useEffect(() => {
    if (!open) {
      setPanelPos(null);
      return;
    }

    const updatePanelPosition = () => {
      const triggerRect = triggerRef.current?.getBoundingClientRect();
      if (!triggerRect) return;

      const viewportPadding = 8;
      const panelWidth = 440;
      const panelHeight = panelRef.current?.offsetHeight ?? Math.min(window.innerHeight * 0.7, 720);
      const left = Math.min(
        Math.max(triggerRect.right - panelWidth, viewportPadding),
        Math.max(viewportPadding, window.innerWidth - panelWidth - viewportPadding),
      );

      // Auto flip logic: prefer provided placement, but flip if it would overflow viewport
      const bottomTop = triggerRect.bottom + viewportPadding;
      const topTop = triggerRect.top - panelHeight - viewportPadding;

      let chosenPlacement = placement;
      if (placement === 'bottom') {
        // if there isn't enough space below, flip to top
        if (bottomTop + panelHeight > window.innerHeight - viewportPadding) chosenPlacement = 'top';
      } else if (placement === 'top') {
        // if there isn't enough space above, flip to bottom
        if (topTop < viewportPadding) chosenPlacement = 'bottom';
      }

      const preferredTop = chosenPlacement === 'top' ? topTop : bottomTop;
      const top = Math.min(Math.max(preferredTop, viewportPadding), Math.max(viewportPadding, window.innerHeight - panelHeight - viewportPadding));

      setPanelPos({ top, left });
    };

    updatePanelPosition();
    const rafId = window.requestAnimationFrame(updatePanelPosition);
    window.addEventListener('resize', updatePanelPosition);
    document.addEventListener('scroll', updatePanelPosition, true);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updatePanelPosition);
      document.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [open, placement, draft, collapsed]);

  const activeCount = (
    ((value.daysOfWeek?.length || 0) > 0 ? 1 : 0) +
    ((value.months?.length || 0) > 0 ? 1 : 0) +
    (value.timeMin ? 1 : 0) +
    (value.timeMax ? 1 : 0) +
    (value.volumeMin != null ? 1 : 0) +
    (value.volumeMax != null ? 1 : 0) +
    (value.pnlMin != null ? 1 : 0) +
    (value.pnlMax != null ? 1 : 0) +
    (value.dayResult ? 1 : 0)
  );
  const isActive = activeCount > 0;

  const FieldLabel = ({ children }: { children: React.ReactNode }) => (
    <div className="text-sm whitespace-nowrap pr-3 text-zinc-700 dark:text-zinc-300">{children}</div>
  );

  const NumInput = ({ value: v, onChange, placeholder }: { value?: number; onChange: (next?: number) => void; placeholder?: string }) => {
    const step = 1;
    const bump = (delta: number) => {
      const cur = typeof v === 'number' && Number.isFinite(v) ? v : 0;
      onChange(cur + delta);
    };
    return (
      <div className="relative inline-flex w-full items-center">
        <input
          type="number"
          inputMode="decimal"
          value={v ?? ''}
          onChange={(e) => {
            const raw = e.target.value.trim();
            onChange(raw === '' ? undefined : Number(raw));
          }}
          placeholder={placeholder}
          className={`${dark ? 'w-full h-9 rounded-md border bg-zinc-900 border-zinc-700 pr-10 pl-3 text-sm text-zinc-100 placeholder-zinc-500' : 'w-full h-9 rounded-md border bg-white border-zinc-300 pr-10 pl-3 text-sm text-zinc-900 placeholder-zinc-400'} num-no-spin`}
        />
        <div
          className={`${dark ? 'absolute right-0.5 top-0.5 bottom-0.5 rounded-md border border-zinc-700 bg-zinc-800/60' : 'absolute right-0.5 top-0.5 bottom-0.5 rounded-md border border-zinc-300 bg-zinc-100/70'} overflow-hidden flex flex-col w-8`}
          role="group"
          aria-label="Adjust value"
        >
          <button
            type="button"
            aria-label="Increment"
            title="Increment"
            onClick={(e) => { e.preventDefault(); bump(step); }}
            className={`${dark ? 'flex-1 grid place-items-center hover:bg-zinc-700/70 active:bg-zinc-700 text-zinc-200' : 'flex-1 grid place-items-center hover:bg-zinc-200 active:bg-zinc-300 text-zinc-700'} relative`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6"/></svg>
            <span className={`${dark ? 'absolute bottom-0 left-1 right-1 h-px bg-zinc-700' : 'absolute bottom-0 left-1 right-1 h-px bg-zinc-300'}`} aria-hidden></span>
          </button>
          <button
            type="button"
            aria-label="Decrement"
            title="Decrement"
            onClick={(e) => { e.preventDefault(); bump(-step); }}
            className={`${dark ? 'flex-1 grid place-items-center hover:bg-zinc-700/70 active:bg-zinc-700 text-zinc-200' : 'flex-1 grid place-items-center hover:bg-zinc-200 active:bg-zinc-300 text-zinc-700'}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          </button>
        </div>
      </div>
    );
  };

  const MinMax = ({ left = 'min', right = 'max', min, max, setMin, setMax }: { left?: string; right?: string; min?: number; max?: number; setMin?: (n?: number) => void; setMax?: (n?: number) => void }) => (
    <div className="flex gap-2 items-center flex-1">
      {setMin ? (
        <NumInput value={min} onChange={setMin} placeholder={left} />
      ) : (
        <input disabled placeholder={left} className={`${dark ? 'w-full h-9 rounded-md border bg-zinc-900 border-zinc-700 px-3 text-sm text-zinc-400 placeholder-zinc-500' : 'w-full h-9 rounded-md border bg-white border-zinc-300 px-3 text-sm text-zinc-400 placeholder-zinc-400'}`} />
      )}
      {setMax ? (
        <NumInput value={max} onChange={setMax} placeholder={right} />
      ) : (
        <input disabled placeholder={right} className={`${dark ? 'w-full h-9 rounded-md border bg-zinc-900 border-zinc-700 px-3 text-sm text-zinc-400 placeholder-zinc-500' : 'w-full h-9 rounded-md border bg-white border-zinc-300 px-3 text-sm text-zinc-400 placeholder-zinc-400'}`} />
      )}
    </div>
  );

  const Row = ({ label: rowLabel, children }: { label: React.ReactNode; children: React.ReactNode }) => (
    <div className="flex items-center gap-3">
      <FieldLabel>{rowLabel}</FieldLabel>
      {children}
    </div>
  );

  const Section = ({ id, title, children }: { id: keyof typeof collapsed; title: string; children: React.ReactNode }) => (
    <div className={`${dark ? 'border-zinc-800' : 'border-zinc-200'} border-t pt-3 first:border-t-0 first:pt-0`}>
      <button
        type="button"
        aria-expanded={!collapsed[id]}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggle(id);
        }}
        className={`w-full flex items-center justify-between rounded-md px-1 py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${dark ? 'text-zinc-100 hover:bg-zinc-800/50' : 'text-zinc-800 hover:bg-zinc-50'}`}
      >
        <div className={`text-sm font-semibold ${dark ? 'text-zinc-100' : 'text-zinc-800'}`}>{title}</div>
        <ChevronDownIcon className={`w-4 h-4 transition-transform ${collapsed[id] ? '' : 'rotate-180'}`} />
      </button>
      {!collapsed[id] && (
        <div className={`mt-3 space-y-3 rounded-lg border relative ${dark ? 'border-blue-600/40 bg-zinc-900/40' : 'border-blue-600/30 bg-zinc-50'}`}>
          <div className={`absolute left-0 top-0 bottom-0 w-1 ${dark ? 'bg-blue-600/50' : 'bg-blue-600/30'}`} aria-hidden></div>
          <div className={`pointer-events-none absolute inset-0 rounded-lg ${dark ? 'ring-1 ring-blue-500/10' : 'ring-1 ring-blue-500/10'}`} aria-hidden></div>
          <div className="p-3 space-y-3">{children}</div>
        </div>
      )}
    </div>
  );

  const WeekdaySelector = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return (
      <div className="flex flex-wrap gap-1">
        {days.map((d, idx) => {
          const selected = draft.daysOfWeek.includes(idx);
          return (
            <button
              key={d}
              type="button"
              className={`px-2 h-8 rounded-md text-sm border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${selected ? 'bg-blue-600 text-white border-blue-600' : (dark ? 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800' : 'bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-100')}`}
              onClick={() => setDraft(prev => ({ ...prev, daysOfWeek: selected ? prev.daysOfWeek.filter(x => x !== idx) : [...prev.daysOfWeek, idx] }))}
            >
              {d}
            </button>
          );
        })}
      </div>
    );
  };

  const MonthSelector = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return (
      <div className="flex flex-wrap gap-1">
        {months.map((m, idx) => {
          const selected = draft.months.includes(idx);
          return (
            <button
              key={m}
              type="button"
              className={`px-2 h-8 rounded-md text-sm border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${selected ? 'bg-blue-600 text-white border-blue-600' : (dark ? 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800' : 'bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-100')}`}
              onClick={() => setDraft(prev => ({ ...prev, months: selected ? prev.months.filter(x => x !== idx) : [...prev.months, idx] }))}
            >
              {m}
            </button>
          );
        })}
      </div>
    );
  };

  const TimeField = ({ value: v, onChange, placeholder = '--:--' }: { value?: string; onChange: (next?: string) => void; placeholder?: string; format?: '12h' | '24h' }) => {
    const [openPop, setOpenPop] = useState(false);
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const [pos, setPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
    const [tempH24, setTempH24] = useState<number | null>(null);
    const [tempM, setTempM] = useState<number | null>(null);

    const updatePosition = useCallback(() => {
      const b = btnRef.current?.getBoundingClientRect();
      if (!b) return;
      const MIN_LEFT = 8;
      const MARGIN = 6;
      const desiredLeft = b.left + window.scrollX;
      const maxLeft = Math.max(MIN_LEFT, window.innerWidth - MIN_LEFT - 260);
      const left = Math.min(Math.max(desiredLeft, MIN_LEFT), maxLeft);
      const top = b.bottom + window.scrollY + MARGIN;
      setPos({ left, top });
    }, []);

    useEffect(() => {
      if (!openPop) return;
      updatePosition();
      const onResize = () => updatePosition();
      const onScroll = () => updatePosition();
      window.addEventListener('resize', onResize);
      document.addEventListener('scroll', onScroll, true);
      return () => {
        window.removeEventListener('resize', onResize);
        document.removeEventListener('scroll', onScroll, true);
      };
    }, [openPop, updatePosition]);

    const parse24 = (s?: string): { h24: number; m: number } | null => {
      if (!s || !/^\d{2}:\d{2}$/.test(s)) return null;
      const [hh, mm] = s.split(':');
      return { h24: Math.max(0, Math.min(23, Number(hh))), m: Math.max(0, Math.min(59, Number(mm))) };
    };
    const canon = parse24(v || undefined);

    useEffect(() => {
      if (openPop) {
        setTempH24(canon ? canon.h24 : null);
        setTempM(canon ? canon.m : null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openPop]);

    const display = (() => {
      if (!canon) return '';
      const mm = String(canon.m).padStart(2, '0');
      const hh = String(canon.h24).padStart(2, '0');
      return `${hh}:${mm}`;
    })();

    const commitApply = () => {
      if (tempH24 == null || tempM == null) {
        onChange(undefined);
      } else {
        const hh = String(tempH24).padStart(2, '0');
        const mm = String(tempM).padStart(2, '0');
        onChange(`${hh}:${mm}`);
      }
      setOpenPop(false);
    };

    const selH24: number | null = tempH24;
    const selM: number | null = tempM;

    return (
      <div className="relative">
        <button
          ref={btnRef}
          type="button"
          onClick={() => { if (!openPop) setOpenPop(true); }}
          className={`${dark ? 'w-full h-9 rounded-md border bg-zinc-900 border-zinc-700 px-3 text-sm text-zinc-100 hover:bg-zinc-800' : 'w-full h-9 rounded-md border bg-white border-zinc-300 px-3 text-sm text-zinc-900 hover:bg-zinc-100'} flex items-center justify-between`}
        >
          <span className={`${display ? '' : (dark ? 'text-zinc-500' : 'text-zinc-400')}`}>{display || placeholder}</span>
          <span className={`${dark ? 'text-zinc-500' : 'text-zinc-400'}`} aria-hidden>🕒</span>
        </button>
        {openPop && ReactDOM.createPortal(
          <div className={`absolute z-[999999] p-2 rounded-md shadow-lg border ${dark ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-zinc-200'} filters-time-pop`} style={{ minWidth: 260, left: pos.left, top: pos.top, position: 'absolute' }}>
            <div className="flex gap-2">
                <div className={`${dark ? 'border-zinc-700' : 'border-zinc-200'} w-20 max-h-48 overflow-auto rounded-md border`}>
                  {Array.from({ length: 24 }, (_, h) => (
                    <button key={h} type="button" className={`w-full text-left px-3 py-1 text-sm ${h === selH24 ? 'bg-blue-600 text-white' : (dark ? 'text-zinc-200 hover:bg-zinc-800' : 'text-zinc-800 hover:bg-zinc-100')}`} onClick={() => setTempH24(h)}>
                      {String(h).padStart(2, '0')}
                    </button>
                  ))}
                </div>
                <div className={`${dark ? 'border-zinc-700' : 'border-zinc-200'} w-20 max-h-48 overflow-auto rounded-md border`}>
                  {Array.from({ length: 60 }, (_, m) => (
                    <button key={m} type="button" className={`w-full text-left px-3 py-1 text-sm ${m === selM ? 'bg-blue-600 text-white' : (dark ? 'text-zinc-200 hover:bg-zinc-800' : 'text-zinc-800 hover:bg-zinc-100')}`} onClick={() => setTempM(m)}>
                      {String(m).padStart(2, '0')}
                    </button>
                  ))}
                </div>
              </div>
            
            <div className="mt-2 flex justify-between">
              <button type="button" className={`${dark ? 'text-zinc-200 hover:text-white' : 'text-zinc-700 hover:text-zinc-900'} text-xs`} onClick={() => { setTempH24(null); setTempM(null); onChange(undefined); }}>
                Clear
              </button>
              <button type="button" className="text-xs text-blue-600 hover:underline" onClick={commitApply}>Apply</button>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  };

  return (
    <div className="relative" ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => { if (suppressToggleRef.current) return; setOpen((v) => !v); }}
        className={`${(open || isActive)
          ? (activeTriggerClassName ?? 'inline-flex items-center gap-2 pl-3 pr-2 h-10 rounded-xl bg-blue-600 text-white hover:bg-blue-500')
          : (triggerClassName ?? (dark
              ? 'inline-flex items-center gap-2 pl-3 pr-2 h-10 rounded-xl border bg-zinc-900 border-zinc-700 text-zinc-100 hover:bg-zinc-800'
              : 'inline-flex items-center gap-2 pl-3 pr-2 h-10 rounded-xl border bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-100'))
        } transition-colors`}
      >
        {/* Sliders icon to match previous button */}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
          <line x1="21" y1="4" x2="14" y2="4"></line>
          <line x1="10" y1="4" x2="3" y2="4"></line>
          <line x1="21" y1="12" x2="12" y2="12"></line>
          <line x1="8" y1="12" x2="3" y2="12"></line>
          <line x1="21" y1="20" x2="16" y2="20"></line>
          <line x1="12" y1="20" x2="3" y2="20"></line>
          <circle cx="12" cy="4" r="2"></circle>
          <circle cx="8" cy="12" r="2"></circle>
          <circle cx="16" cy="20" r="2"></circle>
        </svg>
        <span className="text-sm font-medium">{label}</span>
        {isActive && (
          <span className={`${badgeClassName ?? `inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold ${(open || isActive) ? 'bg-white/20 text-white' : (dark ? 'bg-zinc-800 text-zinc-100' : 'bg-zinc-200 text-zinc-800')}`}`}>
            {activeCount}
          </span>
        )}
        <ChevronDownIcon className={`w-4 h-4 opacity-80 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && panelPos && ReactDOM.createPortal(
        <div
          ref={panelRef}
          className={`filters-panel fixed w-[440px] max-h-[70vh] z-[10060] rounded-xl shadow-lg border ${dark ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-zinc-200'} flex flex-col overflow-hidden`}
          style={{ top: panelPos.top, left: panelPos.left }}
        >
          <div className="p-3 space-y-3 text-sm overflow-auto flex-1">
            <Section id="days" title="Days/Time">
              <Row label="Day of The Week:">
                <WeekdaySelector />
              </Row>
              <Row label="Time of Day:">
                <div className="flex gap-2 items-center flex-1">
                  <TimeField value={draft.timeMin} onChange={(next) => setDraft(prev => ({ ...prev, timeMin: next }))} />
                  <TimeField value={draft.timeMax} onChange={(next) => setDraft(prev => ({ ...prev, timeMax: next }))} />
                </div>
              </Row>
              <Row label="Month:">
                <MonthSelector />
              </Row>
              <Row label="Duration:">
                <div className="flex gap-2 items-center flex-1">
                  <TimeField format="24h" value={draft.durationMin} onChange={(next) => setDraft(prev => ({ ...prev, durationMin: next }))} />
                  <TimeField format="24h" value={draft.durationMax} onChange={(next) => setDraft(prev => ({ ...prev, durationMax: next }))} />
                </div>
              </Row>
            </Section>

            <Section id="price" title="Price/Volume">
              <Row label="Price ($):"><MinMax min={draft.priceMin} max={draft.priceMax} setMin={(n) => setDraft(prev => ({ ...prev, priceMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, priceMax: n }))} /></Row>
              <Row label="Volume Traded:"><MinMax min={draft.volumeMin} max={draft.volumeMax} setMin={(n) => setDraft(prev => ({ ...prev, volumeMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, volumeMax: n }))} /></Row>
              <Row label="In-trade Price Range:"><MinMax min={draft.inTradePriceMin} max={draft.inTradePriceMax} setMin={(n) => setDraft(prev => ({ ...prev, inTradePriceMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, inTradePriceMax: n }))} /></Row>
              <Row label="Executions:"><MinMax min={draft.execCountMin} max={draft.execCountMax} setMin={(n) => setDraft(prev => ({ ...prev, execCountMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, execCountMax: n }))} /></Row>
            </Section>

            <Section id="instrument" title="Instrument">
              <Row label="Gap %:"><MinMax left="% min" right="% max" min={draft.gapMinPct} max={draft.gapMaxPct} setMin={(n) => setDraft(prev => ({ ...prev, gapMinPct: n }))} setMax={(n) => setDraft(prev => ({ ...prev, gapMaxPct: n }))} /></Row>
              <Row label="RVOL:"><MinMax min={draft.rvolMin} max={draft.rvolMax} setMin={(n) => setDraft(prev => ({ ...prev, rvolMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, rvolMax: n }))} /></Row>
              <Row label="ATR:"><MinMax min={draft.atrMin} max={draft.atrMax} setMin={(n) => setDraft(prev => ({ ...prev, atrMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, atrMax: n }))} /></Row>
              <Row label="Day Type:">
                <div className="flex-1">
                  <select aria-label="Day Type" value={draft.dayType ?? ''} onChange={(e) => setDraft(prev => ({ ...prev, dayType: e.target.value || undefined }))} className={`${dark ? 'w-full h-9 rounded-md border bg-zinc-900 border-zinc-700 px-3 text-sm text-zinc-100' : 'w-full h-9 rounded-md border bg-white border-zinc-300 px-3 text-sm text-zinc-900'}`}>
                    <option value="">All</option>
                    <option value="trend">Trend</option>
                    <option value="range">Range</option>
                  </select>
                </div>
              </Row>
              <Row label="Entry % of ATR:"><MinMax left="% min" right="% max" min={draft.entryPctAtrMin} max={draft.entryPctAtrMax} setMin={(n) => setDraft(prev => ({ ...prev, entryPctAtrMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, entryPctAtrMax: n }))} /></Row>
              <Row label="% Move Intraday:"><MinMax left="% min" right="% max" min={draft.moveIntraPctMin} max={draft.moveIntraPctMax} setMin={(n) => setDraft(prev => ({ ...prev, moveIntraPctMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, moveIntraPctMax: n }))} /></Row>
              <Row label="Prior Day RVOL:"><MinMax min={draft.priorDayRvolMin} max={draft.priorDayRvolMax} setMin={(n) => setDraft(prev => ({ ...prev, priorDayRvolMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, priorDayRvolMax: n }))} /></Row>
            </Section>

            <Section id="winloss" title="Win/Loss Trades">
              <Row label="Trade Result:">
                <div className="inline-flex rounded-md overflow-hidden border border-zinc-300 dark:border-zinc-700 text-sm">
                  {(['All', 'Winning', 'Losing'] as const).map((opt) => {
                    const isSel = (opt === 'All' && !draft.dayResult) || (draft.dayResult === opt);
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setDraft((prev) => ({ ...prev, dayResult: opt === 'All' ? undefined : opt }))}
                        className={`${isSel ? (dark ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white') : (dark ? 'bg-zinc-900 text-zinc-300' : 'bg-white text-zinc-700')} px-3 h-9`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </Row>
              <Row label="Day Result:">
                <div className="flex-1">
                  <select aria-label="Day Result" disabled className={`${dark ? 'w-full h-9 rounded-md border bg-zinc-900 border-zinc-700 px-3 text-sm text-zinc-400' : 'w-full h-9 rounded-md border bg-white border-zinc-300 px-3 text-sm text-zinc-500'}`}>
                    <option>All</option>
                  </select>
                </div>
              </Row>
              <Row label="P&L % Gain:"><MinMax left="% min" right="% max" min={draft.pnlPctMin} max={draft.pnlPctMax} setMin={(n) => setDraft(prev => ({ ...prev, pnlPctMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, pnlPctMax: n }))} /></Row>
              <Row label="P&L (R):"><MinMax min={draft.pnlRMin} max={draft.pnlRMax} setMin={(n) => setDraft(prev => ({ ...prev, pnlRMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, pnlRMax: n }))} /></Row>
              <Row label="P&L ($):"><MinMax min={draft.pnlMin} max={draft.pnlMax} setMin={(n) => setDraft(prev => ({ ...prev, pnlMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, pnlMax: n }))} /></Row>
            </Section>

            <Section id="tags" title="Tags">
              <Row label="Tag Win %:">
                <div className="flex-1">
                  <NumInput value={draft.tagWinPctMin} onChange={(n) => setDraft(prev => ({ ...prev, tagWinPctMin: n }))} placeholder="% minimum" />
                </div>
              </Row>
              <Row label="Excluded Tags:">
                <div className="flex-1">
                  <input value={draft.excludedTags ?? ''} onChange={(e) => setDraft(prev => ({ ...prev, excludedTags: e.target.value }))} placeholder="Select tags" className={`${dark ? 'w-full h-9 rounded-md border bg-zinc-900 border-zinc-700 px-3 text-sm text-zinc-100 placeholder-zinc-500' : 'w-full h-9 rounded-md border bg-white border-zinc-300 px-3 text-sm text-zinc-900 placeholder-zinc-400'}`} />
                </div>
              </Row>
              <Row label="Tag Profit Factor:"><div className="flex-1"><NumInput value={draft.tagProfitFactorMin} onChange={(n) => setDraft(prev => ({ ...prev, tagProfitFactorMin: n }))} placeholder="(positive)" /></div></Row>
              <Row label="Tag P&L:"><MinMax min={draft.tagPnlMin} max={draft.tagPnlMax} setMin={(n) => setDraft(prev => ({ ...prev, tagPnlMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, tagPnlMax: n }))} /></Row>
              <Row label="Tag avg. pos. MFE:"><MinMax min={draft.tagMfePosMin} max={draft.tagMfePosMax} setMin={(n) => setDraft(prev => ({ ...prev, tagMfePosMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, tagMfePosMax: n }))} /></Row>
              <Row label="Tag Count:"><MinMax min={draft.tagCountMin} max={draft.tagCountMax} setMin={(n) => setDraft(prev => ({ ...prev, tagCountMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, tagCountMax: n }))} /></Row>
              <Row label="Tag avg. pos. MAE:"><MinMax min={draft.tagMaePosMin} max={draft.tagMaePosMax} setMin={(n) => setDraft(prev => ({ ...prev, tagMaePosMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, tagMaePosMax: n }))} /></Row>
              <Row label="Tag Volume:"><MinMax min={draft.tagVolumeMin} max={draft.tagVolumeMax} setMin={(n) => setDraft(prev => ({ ...prev, tagVolumeMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, tagVolumeMax: n }))} /></Row>
            </Section>

            <Section id="sma" title="Entry Distance to Daily SMA">
              <Row label="5 SMA:"><MinMax left="% min" right="% max" min={draft.sma5MinPct} max={draft.sma5MaxPct} setMin={(n) => setDraft(prev => ({ ...prev, sma5MinPct: n }))} setMax={(n) => setDraft(prev => ({ ...prev, sma5MaxPct: n }))} /></Row>
              <Row label="10 SMA:"><MinMax left="% min" right="% max" min={draft.sma10MinPct} max={draft.sma10MaxPct} setMin={(n) => setDraft(prev => ({ ...prev, sma10MinPct: n }))} setMax={(n) => setDraft(prev => ({ ...prev, sma10MaxPct: n }))} /></Row>
              <Row label="20 SMA:"><MinMax left="% min" right="% max" min={draft.sma20MinPct} max={draft.sma20MaxPct} setMin={(n) => setDraft(prev => ({ ...prev, sma20MinPct: n }))} setMax={(n) => setDraft(prev => ({ ...prev, sma20MaxPct: n }))} /></Row>
              <Row label="50 SMA:"><MinMax left="% min" right="% max" min={draft.sma50MinPct} max={draft.sma50MaxPct} setMin={(n) => setDraft(prev => ({ ...prev, sma50MinPct: n }))} setMax={(n) => setDraft(prev => ({ ...prev, sma50MaxPct: n }))} /></Row>
              <Row label="100 SMA:"><MinMax left="% min" right="% max" min={draft.sma100MinPct} max={draft.sma100MaxPct} setMin={(n) => setDraft(prev => ({ ...prev, sma100MinPct: n }))} setMax={(n) => setDraft(prev => ({ ...prev, sma100MaxPct: n }))} /></Row>
              <Row label="200 SMA:"><MinMax left="% min" right="% max" min={draft.sma200MinPct} max={draft.sma200MaxPct} setMin={(n) => setDraft(prev => ({ ...prev, sma200MinPct: n }))} setMax={(n) => setDraft(prev => ({ ...prev, sma200MaxPct: n }))} /></Row>
            </Section>

            <Section id="stats" title="Statistics">
              <Row label="Best Exit P&L ($):"><MinMax min={draft.bestExitPnlMin} max={draft.bestExitPnlMax} setMin={(n) => setDraft(prev => ({ ...prev, bestExitPnlMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, bestExitPnlMax: n }))} /></Row>
              <Row label="Position MAE:"><MinMax min={draft.posMaeMin} max={draft.posMaeMax} setMin={(n) => setDraft(prev => ({ ...prev, posMaeMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, posMaeMax: n }))} /></Row>
              <Row label="Price MAE:"><MinMax min={draft.priceMaeMin} max={draft.priceMaeMax} setMin={(n) => setDraft(prev => ({ ...prev, priceMaeMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, priceMaeMax: n }))} /></Row>
              <Row label="Time to pos. MAE:"><MinMax min={draft.timeToPosMaeMin} max={draft.timeToPosMaeMax} setMin={(n) => setDraft(prev => ({ ...prev, timeToPosMaeMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, timeToPosMaeMax: n }))} /></Row>
              <Row label="Time to Price MAE:"><MinMax min={draft.timeToPriceMaeMin} max={draft.timeToPriceMaeMax} setMin={(n) => setDraft(prev => ({ ...prev, timeToPriceMaeMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, timeToPriceMaeMax: n }))} /></Row>
              <Row label="Best Exit P&L (R):"><MinMax min={draft.bestExitRMin} max={draft.bestExitRMax} setMin={(n) => setDraft(prev => ({ ...prev, bestExitRMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, bestExitRMax: n }))} /></Row>
              <Row label="Position MFE:"><MinMax min={draft.posMfeMin} max={draft.posMfeMax} setMin={(n) => setDraft(prev => ({ ...prev, posMfeMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, posMfeMax: n }))} /></Row>
              <Row label="Price MFE:"><MinMax min={draft.priceMfeMin} max={draft.priceMfeMax} setMin={(n) => setDraft(prev => ({ ...prev, priceMfeMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, priceMfeMax: n }))} /></Row>
              <Row label="Time to pos. MFE:"><MinMax min={draft.timeToPosMfeMin} max={draft.timeToPosMfeMax} setMin={(n) => setDraft(prev => ({ ...prev, timeToPosMfeMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, timeToPosMfeMax: n }))} /></Row>
              <Row label="Time to Price MFE:"><MinMax min={draft.timeToPriceMfeMin} max={draft.timeToPriceMfeMax} setMin={(n) => setDraft(prev => ({ ...prev, timeToPriceMfeMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, timeToPriceMfeMax: n }))} /></Row>
              <Row label="Initial Risk:"><MinMax min={draft.initialRiskMin} max={draft.initialRiskMax} setMin={(n) => setDraft(prev => ({ ...prev, initialRiskMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, initialRiskMax: n }))} /></Row>
              <Row label="Exit Efficiency (%):"><MinMax left="% min" right="% max" min={draft.exitEfficiencyMinPct} max={draft.exitEfficiencyMaxPct} setMin={(n) => setDraft(prev => ({ ...prev, exitEfficiencyMinPct: n }))} setMax={(n) => setDraft(prev => ({ ...prev, exitEfficiencyMaxPct: n }))} /></Row>
              <Row label="Comissions & Fee's:"><MinMax min={draft.commissionsMin} max={draft.commissionsMax} setMin={(n) => setDraft(prev => ({ ...prev, commissionsMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, commissionsMax: n }))} /></Row>
              <Row label="P&L % of MAE:"><MinMax left="% min" right="% max" min={draft.pnlPctOfMaeMin} max={draft.pnlPctOfMaeMax} setMin={(n) => setDraft(prev => ({ ...prev, pnlPctOfMaeMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, pnlPctOfMaeMax: n }))} /></Row>
              <Row label="P&L % of MFE:"><MinMax left="% min" right="% max" min={draft.pnlPctOfMfeMin} max={draft.pnlPctOfMfeMax} setMin={(n) => setDraft(prev => ({ ...prev, pnlPctOfMfeMin: n }))} setMax={(n) => setDraft(prev => ({ ...prev, pnlPctOfMfeMax: n }))} /></Row>
            </Section>
          </div>

          <div className={`p-3 flex items-center justify-between gap-2 border-t border-zinc-200 dark:border-zinc-800 sticky bottom-0 ${dark ? 'bg-zinc-900' : 'bg-white'}`}>
            <button
              className={`${dark ? 'px-3 h-9 rounded-lg text-sm bg-zinc-800 text-zinc-100 hover:bg-zinc-700' : 'px-3 h-9 rounded-lg text-sm bg-zinc-100 text-zinc-800 hover:bg-zinc-200'}`}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const empty = emptyJournalFilters();
                setDraft(empty);
                onApply(empty);
                suppressToggleRef.current = true;
                setOpen(false);
                setTimeout(() => { suppressToggleRef.current = false; }, 0);
              }}
            >
              Reset
            </button>
            <div className="flex items-center gap-2">
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  suppressToggleRef.current = true;
                  setOpen(false);
                  setTimeout(() => { suppressToggleRef.current = false; }, 0);
                }}
                className={`${dark ? 'px-3 h-9 rounded-lg border bg-zinc-900 border-zinc-700 text-sm text-zinc-200 hover:bg-zinc-800' : 'px-3 h-9 rounded-lg border bg-white border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-100'}`}
              >
                Cancel
              </button>
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onApply(draft);
                  suppressToggleRef.current = true;
                  setOpen(false);
                  setTimeout(() => { suppressToggleRef.current = false; }, 0);
                }}
                className="px-3 h-9 rounded-lg text-sm text-white bg-blue-600 hover:bg-blue-500"
              >
                Apply
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
